// @ts-nocheck
import React, { useContext } from "react";
import "../styles/cbImg.css";
import appConst from "../Utilities/appConst.json";
import { LanguageContext } from "../context/language/LanguageContext";
import { useSelector } from "react-redux";
import iconPostOffice from "../img/shipping_icon_3.svg";
import iconPostOfficeMini from "../img/shipping_icon_3_mini.svg";
import iconLockers from "../img/shipping_icon_17.svg";
import iconLockersMini from "../img/shipping_icon_17_mini.svg";
import iconPickUp from "../img/shipping_icon_21.svg";
import iconPickUpMini from "../img/shipping_icon_21_mini.svg";
import iconHome from "../img/shipping_icon_23.svg";
import iconHomeMini from "../img/shipping_icon_23_mini.svg";
import iconTapuz from "../img/Tapuz.svg";
import iconLockersAlt from "../img/Lockers.svg";
import { resolveShippingTitle } from "../Utilities/shippingTitle";
import type { ShippingOption } from "../types/shipping";

type ShippingMethodProps = {
  option: ShippingOption;
  onClick?: (option: ShippingOption) => void;
  isChecked: boolean;
  product?: string | null;
  testId?: string;
};

type ShippingMethodState = {
  mode: { direction: string };
  user: { isNewsletter: boolean };
};

// Shipping method type mappings
const SHIPPING_METHODS = {
  HOME_DELIVERY: {
    ids: ["23"],
    keywords: ["home"],
    icon: iconHome,
    miniIcon: iconHomeMini,
  },
  PICKUP: {
    ids: ["21", "19", "12"],
    keywords: ["pickup", "asafta"],
    icon: iconPickUp,
    miniIcon: iconPickUpMini,
  },
  LOCKERS: {
    ids: ["17", "8"],
    keywords: ["locker", "boxit"],
    icon: iconLockers,
    miniIcon: iconLockersMini,
  },
  POST_OFFICE: {
    ids: ["4", "3"],
    keywords: ["register", "post"],
    icon: iconPostOffice,
    miniIcon: iconPostOfficeMini,
  },
  TAPUZ: {
    ids: [],
    keywords: ["tapuz"],
    icon: iconTapuz,
  },
  LOCKERS_ALT: {
    ids: [],
    keywords: ["locker"],
    icon: iconLockersAlt,
  },
};

const resolveShippingIcon = (option: ShippingOption) => {
  const id = option?.id?.toString();
  const name = (option?.name || option?.displayName || "").toLowerCase();
  const clean = name.replace(/[^a-z]/g, "");

  // Check each shipping method type
  for (const method of Object.values(SHIPPING_METHODS)) {
    const matchesId = method.ids.includes(id);
    const matchesKeyword = method.keywords.some((keyword) =>
      clean.includes(keyword)
    );

    if (matchesId || matchesKeyword) {
      return {
        desktopIcon: method.icon,
        mobileIcon: method.miniIcon || method.icon,
      };
    }
  }

  // Default fallback
  return {
    desktopIcon: iconPickUp,
    mobileIcon: iconPickUpMini,
  };
};

const resolveShippingSubtitle = ({
  estimatedDate,
  id,
  estimatedDateLabel,
}: {
  estimatedDate?: string;
  id: string | number;
  estimatedDateLabel: string;
}) => {
  return `${estimatedDateLabel} ${estimatedDate || ""}`.trim();
};

export default function ShippingMethod({
  option,
  onClick,
  isChecked,
  product,
  testId,
}: ShippingMethodProps) {
  const languageContext = useContext(LanguageContext);
  const direction = useSelector(
    (state: ShippingMethodState) => state.mode.direction
  );
  const isNewsletter = useSelector(
    (state: ShippingMethodState) => state.user.isNewsletter
  );

  const price = parseFloat(option.price).toFixed(2);
  const priceForClubMember = parseFloat(option.priceForClubMember).toFixed(2);
  const isRtl = direction === "rtl";
  const isAvailable = option.isAvailable;
  const isClickable = Boolean(onClick);

  const rootDirection = isRtl ? "flex-row-reverse" : "flex-row";
  const shopDirection = isRtl ? "flex-row" : "flex-row-reverse";
  const textTone = isAvailable ? "text-secondary-foreground" : "text-disable";
  const mobileGridColumns = isRtl
    ? "grid-cols-[auto_minmax(0,1fr)]"
    : "grid-cols-[minmax(0,1fr)_auto]";
  const mobileContentOrder = isRtl ? "order-2" : "order-1";
  const mobilePriceOrder = isRtl ? "order-1" : "order-2";

  const { desktopIcon, mobileIcon } = resolveShippingIcon(option);
  const shippingTitle = resolveShippingTitle({
    id: option?.id,
    getTranslation: (key) => languageContext.getTranslation(key),
    fallbackTitle: option.displayName || "",
  });
  const shippingSubtitle = resolveShippingSubtitle({
    id: option?.id,
    estimatedDate: option?.estimatedDate,
    estimatedDateLabel: languageContext.getTranslation(appConst.ESTIMATED_DATE),
  });

  return (
    <div
      dir="ltr"
      data-testid={testId}
      className={`shipping_methods text-start relative grid w-full box-border items-start gap-x-3 px-3 py-3 md:flex md:h-[114px] md:items-center md:gap-6 md:px-4 ${mobileGridColumns} ${rootDirection} ${textTone} ${
        isChecked ? "bg-lupa-highlight" : "bg-white"
      } ${
        isClickable
          ? `${isChecked ? "md:hover:bg-lupa-highlight" : "md:hover:bg-lupa-hover"} cursor-pointer`
          : "cursor-default"
      }`}
      onClick={isClickable ? () => onClick(option) : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick(option);
              }
            }
          : undefined
      }
    >
      <div className={`shop m-0 flex min-w-0 flex-1 basis-0 items-center gap-3 ${mobileContentOrder} md:order-none ${shopDirection}`}>
        <div
          className="shipping_method min-w-0 flex-1 text-start"
          dir={direction}
        >
          <div className="flex min-w-0 items-center justify-start gap-2">
            <img
              className="shipping_methods_img h-6 w-6 shrink-0 self-center object-contain md:hidden"
              src={mobileIcon}
              alt={shippingTitle}
            />
            <h3 className="m-0 text-start font-ploni-demibold text-base leading-none text-secondary-foreground md:text-xl">
              {shippingTitle}
            </h3>
          </div>
          <p className="mt-3 mb-0 text-start font-ploni text-sm leading-none text-secondary-foreground md:text-lg">
            {shippingSubtitle}
          </p>
        </div>

        <img
          className="shipping_methods_img hidden shrink-0 self-center object-contain md:block md:h-24 md:w-24"
          src={desktopIcon}
          alt={shippingTitle}
        />
      </div>

      {isNewsletter && price > priceForClubMember ? (
        <div className={`price min-w-fit shrink-0 self-center whitespace-nowrap px-1 text-end md:px-0 ${mobilePriceOrder} md:order-none`}>
          <p className="m-0 line-through font-ploni text-sm leading-none md:text-lg">₪{price}</p>
          <h3 className="m-0 font-ploni-demibold text-base leading-none md:text-xl">₪{option.priceForClubMember !== null ? priceForClubMember : ""}</h3>
        </div>
      ) : (
        <div className={`price min-w-fit shrink-0 self-center whitespace-nowrap px-1 text-end md:px-0 ${mobilePriceOrder} md:order-none`}>
          <h3 className="m-0 font-ploni-demibold text-base leading-none md:text-xl">₪{option.price !== null ? price : ""}</h3>
        </div>
      )}
    </div>
  );
}
